/** Zwevende balk van de lopende missie. */
import { Check, Pause, Play, Square } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { formatClock } from '../../domain/timer';
import { useTimer, useToastLayout } from '../../state/AppProviders';

const btn =
  'inline-flex min-h-11 min-w-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-2xl px-3 text-sm font-semibold transition-all duration-150 active:scale-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current';

export default function TimerBar() {
  const { timer, remainingMs, isPaused, pause, resume, complete, stop } = useTimer();
  const { setBottomOffset } = useToastLayout();
  const ref = useRef<HTMLDivElement>(null);
  const has = !!timer;

  useEffect(() => {
    const el = ref.current;
    if (!has || !el) {
      setBottomOffset(null);
      return;
    }
    const report = () => setBottomOffset(Math.ceil(window.innerHeight - el.getBoundingClientRect().top));
    report();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(report) : null;
    ro?.observe(el);
    window.addEventListener('resize', report);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', report);
      setBottomOffset(null);
    };
  }, [has, setBottomOffset]);

  if (!timer) return null;
  const done = !isPaused && remainingMs <= 0;

  return (
    <div
      ref={ref}
      role="region"
      aria-label="Lopende missie"
      className="fixed inset-x-0 bottom-0 z-40 px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
    >
      <div className="mx-auto flex max-w-xl flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl border border-white/10 bg-slate-900/95 p-3 text-slate-50 backdrop-blur-md dark:bg-slate-800/95">
        <div className="min-w-0 flex-1 basis-40">
          <p className="truncate text-sm font-bold">{timer.activity.naam}</p>
          <p aria-live="polite" className="text-xs text-slate-300">
            {done ? 'Tijd is om' : isPaused ? 'Gepauzeerd' : 'Bezig'}
          </p>
        </div>
        <span role="timer" aria-live="off" className="font-mono text-2xl font-bold tabular-nums">
          {formatClock(remainingMs)}
        </span>
        <div className="flex items-center gap-1.5">
          <button type="button" onClick={complete} className={`${btn} bg-emerald-300 text-emerald-950`} aria-label="Klaar: missie afronden">
            <Check size={16} aria-hidden="true" /> Klaar
          </button>
          {!done ? (
            <button
              type="button"
              onClick={isPaused ? resume : pause}
              className={`${btn} bg-white/10 hover:bg-white/15`}
              aria-label={isPaused ? 'Hervatten' : 'Pauzeren'}
            >
              {isPaused ? <Play size={16} aria-hidden="true" /> : <Pause size={16} aria-hidden="true" />}
              <span className="max-[380px]:sr-only">{isPaused ? 'Hervat' : 'Pauze'}</span>
            </button>
          ) : null}
          <button type="button" onClick={stop} className={`${btn} bg-white/10 hover:bg-white/15`} aria-label="Stoppen zonder loggen">
            <Square size={16} aria-hidden="true" />
            <span className="max-[380px]:sr-only">Stop</span>
          </button>
        </div>
      </div>
    </div>
  );
}
